import { uploadFile } from '@/lib/upload-file';

// Uploads a wastage photo to S3 via the presigned-URL flow and returns the
// durable, hosted file URL to store as the wastage item's `photo_url`.
//
// Regression: GitHub issue #249 — the Record Wastage modal called
// URL.createObjectURL(file) and sent that `blob:http://localhost:3002/…` string
// straight through as `photo_url`. Nothing was ever uploaded: the object URL is
// scoped to the tab that created it and dies on reload, so the wastage entry
// carried no viewable photographic evidence. This mirrors the expense-receipt /
// social-image flow: presign, PUT the bytes to S3, return the hosted `file_url`.

export async function uploadWastagePhoto(
  file: File,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const { file_url } = await uploadFile(file, 'wastage-photo', fetchImpl);
  return file_url;
}

// Raised when a photo upload was attempted but failed, so the caller can tell it
// apart from a create error and abort submission instead of silently recording
// wastage with a missing (or, worse, a blob:) photo the user believes is attached.
export class PhotoUploadError extends Error {
  constructor() {
    super('Photo upload failed');
    this.name = 'PhotoUploadError';
  }
}

/**
 * Resolves the `photo_url` to send for each wastage row. Rows without a captured
 * file resolve to undefined; rows with one are uploaded and resolve to the hosted
 * S3 URL. A failure anywhere throws PhotoUploadError so the caller aborts the
 * whole submission rather than persisting a photo-less record.
 */
export async function resolvePhotoUrls(
  files: Array<File | null>,
  upload: (file: File) => Promise<string> = uploadWastagePhoto,
): Promise<Array<string | undefined>> {
  try {
    return await Promise.all(
      files.map(async (file) => (file ? await upload(file) : undefined)),
    );
  } catch {
    throw new PhotoUploadError();
  }
}
